import React from 'react';
import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { LayoutSingleColumn, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { propTypes } from '../../util/types';
import {
  buildVenueSubpageSchema,
  extractFaqsFromBlocks,
} from '../../util/seoDiscoverySchema';
import SeoBreadcrumbs from '../../components/SeoDiscovery/SeoBreadcrumbs';
import SeoContentSections from '../../components/SeoDiscovery/SeoContentSections';
import SeoEventCalendar from '../../components/SeoDiscovery/SeoEventCalendar';
import SeoInternalLinks from '../../components/SeoDiscovery/SeoInternalLinks';
import SeoUpcomingEvents from '../../components/SeoDiscovery/SeoUpcomingEvents';
import SeoPartnerSlots from '../../components/SeoDiscovery/SeoPartnerSlots';
import SeoFaqSection from '../../components/SeoDiscovery/SeoFaqSection';
import SeoEditorialTrust from '../../components/SeoDiscovery/SeoEditorialTrust';
import SeoPoiMap from '../../components/SeoDiscovery/SeoPoiMap';
import { POI_MAP_SUBPAGES } from '../../components/SeoDiscovery/seoPoiMapUtils';
import css from '../../components/SeoDiscovery/SeoDiscovery.module.css';

const rootUrl = () =>
  typeof window !== 'undefined'
    ? window.location.origin
    : (process.env.REACT_APP_MARKETPLACE_ROOT_URL || '').replace(/\/$/, '');

const absoluteUrl = url => {
  if (!url) {
    return null;
  }
  return url.startsWith('http') ? url : `${rootUrl()}${url.startsWith('/') ? '' : '/'}${url}`;
};

const venueSocialSharing = (venue, title, description) => {
  const imageUrl = absoluteUrl(venue.image?.url);
  if (!imageUrl) {
    return { title, description };
  }
  const imageAlt = venue.image?.alt || `${venue.venueName} venue image`;
  return {
    title,
    description,
    images1200: [{ name: imageAlt, url: imageUrl, width: 1200, height: 630 }],
    images600: [{ name: imageAlt, url: imageUrl, width: 600, height: 314 }],
  };
};

const EVENTS_WIDGET_SUBPAGES = new Set(['events', 'seating-chart', 'past-events']);

export const SeoDiscoveryVenueSubpageComponent = props => {
  const { payload, fetchInProgress, fetchError, staticContext } = props;

  if (fetchError?.status === 404 && staticContext) {
    staticContext.notfound = true;
  }

  if (fetchInProgress && !payload) {
    return null;
  }
  if (!payload?.venue) {
    return null;
  }

  const {
    venue,
    venueFacts,
    editorialTrust,
    meta,
    contentBlocks,
    internalLinks,
    upcomingEvents,
    breadcrumbs,
    page,
    subpageSlug,
  } = payload;
  const canonicalPath = page?.canonicalPath;
  const noIndex = page?.indexState && page.indexState !== 'index';
  const partnerBlock = (contentBlocks || []).find(b => b.blockType === 'partner_slot');
  const isPlacesToStay = subpageSlug === 'places-to-stay';
  const isEventsPage = subpageSlug === 'events';
  const isSeatingChartPage = subpageSlug === 'seating-chart';
  const contentSectionBlocks = (contentBlocks || []).filter(
    b => b.blockType === 'section' || b.blockType === 'hero'
  );
  const seatingLeadBlocks = isSeatingChartPage ? contentSectionBlocks.slice(0, 1) : [];
  const seatingDetailBlocks = isSeatingChartPage ? contentSectionBlocks.slice(1) : [];
  const isPoiMapPage = POI_MAP_SUBPAGES.has(subpageSlug);
  const poiLeadBlocks = isPoiMapPage ? contentSectionBlocks.slice(0, 1) : [];
  const poiDetailBlocks = isPoiMapPage ? contentSectionBlocks.slice(1) : [];
  const poiMap = (
    <SeoPoiMap venue={venue} venueFacts={venueFacts} subpageSlug={subpageSlug} />
  );
  const pageTitle = meta?.metaTitle;
  const pageDescription = meta?.metaDescription;
  const faqs = extractFaqsFromBlocks(contentBlocks);
  const venueHubPath = `/event-discovery/venues/${venue.slug}/`;
  const includeUpcomingEvents =
    EVENTS_WIDGET_SUBPAGES.has(subpageSlug) || isEventsPage || isSeatingChartPage;
  const schema = buildVenueSubpageSchema({
    venue,
    venueHubPath,
    canonicalPath,
    rootUrl: rootUrl(),
    breadcrumbs,
    faqs,
    upcomingEvents,
    meta: { ...meta, metaTitle: pageTitle, metaDescription: pageDescription },
    includeUpcomingEvents,
  });
  const seatingChartEvents = (
    <SeoUpcomingEvents
      ctaLabel="View seating chart"
      emptyMessage={`There are no live listings at ${venue.venueName} right now. Check back soon to view event-specific seating charts as new events are listed.`}
      events={upcomingEvents}
      intro={`Seating charts can change by event, production layout, and ticket inventory. Choose an event below to open the listing page and view the available seat map when a seating chart is available.`}
      title="View seating charts by event"
      venueName={venue.venueName}
    />
  );

  return (
    <Page
      title={pageTitle}
      description={pageDescription}
      socialSharing={venueSocialSharing(venue, pageTitle, pageDescription)}
      schema={schema}
      scrollingDisabled={false}
    >
      {noIndex ? (
        <Helmet>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
      ) : null}
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <article className={css.root}>
          <SeoBreadcrumbs items={breadcrumbs} />
          <h1 className={css.h1}>{meta?.h1}</h1>
          <SeoEditorialTrust trust={editorialTrust} />
          {isPlacesToStay ? (
            <SeoPartnerSlots
              partnerBlock={partnerBlock}
              showHeading={false}
              subpageSlug={subpageSlug}
              venue={venue}
            />
          ) : null}
          {isSeatingChartPage ? (
            <>
              <SeoContentSections blocks={seatingLeadBlocks} />
              {seatingChartEvents}
              <SeoContentSections blocks={seatingDetailBlocks} />
            </>
          ) : isEventsPage ? (
            <>
              <SeoEventCalendar events={upcomingEvents} venueName={venue.venueName} />
              <SeoContentSections blocks={contentSectionBlocks} />
            </>
          ) : isPoiMapPage ? (
            <>
              <SeoContentSections
                blocks={poiLeadBlocks.length ? poiLeadBlocks : contentSectionBlocks}
                suppressFirstHeading={isPlacesToStay}
              />
              {poiMap}
              {poiDetailBlocks.length ? <SeoContentSections blocks={poiDetailBlocks} /> : null}
            </>
          ) : (
            <SeoContentSections blocks={contentBlocks} suppressFirstHeading={isPlacesToStay} />
          )}
          {!isPlacesToStay ? (
            <SeoPartnerSlots partnerBlock={partnerBlock} subpageSlug={subpageSlug} venue={venue} />
          ) : null}
          <SeoFaqSection blocks={contentBlocks} />
          {EVENTS_WIDGET_SUBPAGES.has(subpageSlug) ? (
            <SeoUpcomingEvents
              events={(upcomingEvents || []).slice(0, subpageSlug === 'events' ? upcomingEvents.length : 4)}
              intro={
                subpageSlug === 'past-events'
                  ? `These upcoming listings show the kinds of events ${venue.venueName} hosts. Browse current inventory for tickets you can buy now.`
                  : undefined
              }
              venueName={venue.venueName}
            />
          ) : null}
          <SeoInternalLinks links={internalLinks} title="More about this venue" />
        </article>
      </LayoutSingleColumn>
    </Page>
  );
};

SeoDiscoveryVenueSubpageComponent.propTypes = {
  payload: object,
  fetchInProgress: bool,
  fetchError: propTypes.error,
  staticContext: object,
};

const mapStateToProps = state => {
  const s = state.SeoDiscoveryVenueSubpage || {};
  return {
    payload: s.payload,
    fetchInProgress: s.fetchInProgress,
    fetchError: s.fetchError,
  };
};

export default compose(withRouter, connect(mapStateToProps))(SeoDiscoveryVenueSubpageComponent);
